<script setup lang="ts">
false && /*#__PURE__*/ defineI18nRoute({ paths: { pl: "/[type]/[slug]" } });

const route = useRoute();
const router = useRouter();

const postSlug = computed(() => route.params.slug as string);
const typeSlug = computed(() => route.params.type as string);

const previewToken = computed(() => route.query.token as string | undefined);

const {
  getPostTypeFromSlug,
  getPostTypeSlug,
  getPostTypeTitleSingular,
  getPostTypeTitlePlural,
} = usePostTypes();

const postType = computed(() => getPostTypeFromSlug(typeSlug));

if (!isDefined(postSlug)) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
    unhandled: false,
    message: "PAGE_NOT_FOUND",
  });
}

const { postFieldPresets, getPost, getPostLink } = usePosts();
const { sharePost } = usePostShare();
const { savePost, savePostAfterLogin } = usePostSave();

const { data } = await useAsyncData(
  `post:${postSlug.value}`,
  () =>
    getPost(postSlug, { fields: postFieldPresets.content }, previewToken).catch(
      () => null
    ),
  {
    default: () => null,
    watch: [postSlug],
  }
);

if (!data.value?.type) {
  const { getRedirect } = useRedirects();

  // check if redirect is defined
  const { data: redirect } = await useAsyncData(
    `post:${postSlug.value}:redirect`,
    () => getRedirect(postSlug)
  );

  if (redirect.value?.post) {
    await navigateTo(
      getPostLink(redirect.value.post.type, redirect.value.post.slug),
      { redirectCode: 301, replace: true }
    );
    throw () => {};
  }

  const { search } = useMeiliSearch("posts");

  // check if similar post exists and redirect to it
  const { data: similarPost } = await useAsyncData(
    `post:${postSlug.value}:similar`,
    () =>
      search(postSlug.value, {
        limit: 1,
        attributesToSearchOn: ["slug", "title", "subtitle"],
      })
        .then((r) => r.hits[0] || null)
        .catch(() => null)
  );

  if (similarPost.value) {
    await navigateTo(
      getPostLink(similarPost.value.type, similarPost.value.slug),
      { redirectCode: 301, replace: true }
    );
    throw () => {};
  }

  await navigateTo("/", { redirectCode: 301, replace: true });
  throw () => {};

  // throw createError({
  //   statusCode: 404,
  //   statusMessage: "Post not found",
  //   unhandled: false,
  //   message: "POST_NOT_FOUND",
  // });
}
const title = computed(() => {
  if (data.value?.meta_title) return data.value.meta_title;

  const suffix =
    (data.value?.type &&
      {
        [PostType.Architect]: "- polecani architekci",
        [PostType.Professional]: "",
        [PostType.Relaxation]: "na wynajem",
        [PostType.Contractor]: "",
        [PostType.Project]: " - gotowy projekt domu",
        [PostType.Inspiration]: "",
      }[data.value.type]) ||
    undefined;

  return (
    [data.value?.title, data.value?.subtitle, suffix]
      .filter(Boolean)
      .join(" ") || null
  );
});

const description = computed(() => {
  if (data.value?.meta_description) return data.value.meta_description;

  return (
    (data.value?.type &&
      {
        [PostType.Architect]: `Przekształć marzenia w rzeczywistość z zaufanym architektem. Stwórz projekt 🏠 dopasowany do Twoich potrzeb z ${data.value.title} ${data.value.subtitle}.`,
        [PostType.Professional]: `Poznaj naszego partnera - firmę ${data.value.title} ${data.value.subtitle}. Dowiedz się, w czym może Tobie pomóc przy budowie lub wykończeniu domu 🏠`,
        [PostType.Relaxation]: `${data.value.title} ${data.value.subtitle} na wynajem w atrakcyjnej cenie. Sprawdź szczegóły oferty i zdecyduj się na wypoczynek w wyjątkowym miejscu.`,
        [PostType.Contractor]: `Poznaj naszego partnera - firmę ${data.value.title} ${data.value.subtitle}. Dowiedz się, w czym może Tobie pomóc przy budowie lub wykończeniu domu 🏠`,
        [PostType.Project]: `Kup projekt domu ${data.value.title} ${data.value.subtitle}. Dowiedź się wszystkiego o projekcie ${data.value.title} ${data.value.subtitle} i stwórz swój wymarzony 🏠 dom.`,
        [PostType.Inspiration]: `Odwiedź podstronę ${data.value.title} ${data.value.subtitle} na stronie nowoczesnastodola.pl. Znajdź wszystkie niezbędne informacje.`,
      }[data.value.type]) ||
    undefined
  );
});

const { titleTemplate } = useSeo();
const ogTitle = computed(() => titleTemplate(title.value || undefined));

useSeoMeta({
  title,
  ogTitle,
  description,
  ogDescription: description,
  twitterDescription: description,
});

defineOgImageComponent("OgImagePost", { post: data });

if (data.value.type !== postType.value) {
  const postTypeSlug = getPostTypeSlug(data.value.type);
  await navigateTo(`/${postTypeSlug}/${postSlug.value}`);
}

const { prev, next } = useAdjacentPosts(data);

const {
  getAllPostMedia,
  lightboxState,
  openLightbox,
  getImageUrl,
  getFileUrl,
} = usePostGallery();

const allMedia = computed(() => getAllPostMedia(data) || []);

const breadcrumbs = useBreadcrumbItems({
  overrides: [
    undefined,
    { label: getPostTypeTitlePlural(data.value.type) },
    { label: data.value.title || getPostTypeTitleSingular(data.value.type) },
  ],
});

const config = useRuntimeConfig();

const address = computed(() => {
  return data.value?.cta_actions?.find((action) => action.type === "location")
    ?.value;
});

const phone = computed(() => {
  return data.value?.cta_actions?.find((action) => action.type === "phone")
    ?.value;
});

const email = computed(() => {
  return data.value?.cta_actions?.find((action) => action.type === "email")
    ?.value;
});

useSchemaOrg([
  defineArticle({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.value.title,
    image: allMedia.value
      .filter((item) => item.collection === "images")
      .map((image) => getImageUrl(image.item)),
    datePublished: data.value.date_published,
    dateModified: data.value.modified,
    author: data.value.author
      ? [{ "@type": "Person", name: data.value.author.name }]
      : [],
    thumbnailUrl: data.value.cover ? getImageUrl(data.value.cover) : undefined,
  }),

  ...([PostType.Relaxation].includes(data.value.type)
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Hotel",
          "@id": config.public.baseUrl + route.path + "/#hotel",
          name: [
            [data.value.title, data.value.subtitle].filter(Boolean).join(" "),
            data.value.location_address,
          ]
            .filter(Boolean)
            .join(", "),
          identifier: data.value.id,
          address: address.value,
          telephone: phone.value,
          email: email.value,
        },
      ]
    : []),

  ...([PostType.Architect, PostType.Professional, PostType.Relaxation].includes(
    data.value.type
  )
    ? [
        {
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "@id": config.public.baseUrl + route.path + "/#profile",
          dateCreated: data.value.created,
          dateModified: data.value.date_modified,
          mainEntity: {
            "@type": "Organization",
            name: data.value.title,
            identifier: data.value.id,
            image: allMedia.value
              .filter((item) => item.collection === "images")
              .map((image) => getImageUrl(image.item)),
            sameAs: data.value.website_link
              ? [data.value.website_link]
              : undefined,
          },
        },
      ]
    : []),

  ...allMedia.value
    .filter((item) => item.collection === "images")
    .map((image) =>
      defineImage({
        "@context": "https://schema.org",
        "@type": "ImageObject",
        url: getImageUrl(image.item),
        contentUrl: getImageUrl(image.item),
        caption: image.item.file?.description || image.item.caption,
        description: image.item.file?.description || image.item.caption,
        height: image.item.height,
        width: image.item.width,
        inLanguage: "pl",

        license: "https://creativecommons.org/licenses/by-nc/4.0/",
        creditText: image.item.caption,
        acquireLicensePage:
          config.public.baseUrl +
          router.resolve(useLocalePath()({ name: "contact" })).href,
      })
    ),

  ...allMedia.value
    .filter((item) => item.collection === "videos")
    .map((video) =>
      defineVideo({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: [data.value?.title, data.value?.subtitle]
          .filter(Boolean)
          .join(" "),
        description: video.item.file?.description || video.item.caption,
        thumbnail: video.item.poster
          ? {
              "@context": "https://schema.org",
              "@type": "ImageObject",
              url: getFileUrl(video.item.poster),
              contentUrl: getFileUrl(video.item.poster),
              description: video.item.file?.description || video.item.caption,
              caption:
                video.item.poster.description ||
                video.item.caption ||
                [data.value?.title, data.value?.subtitle]
                  .filter(Boolean)
                  .join(" "),
              height: video.item.poster.height,
              width: video.item.poster.width,
            }
          : undefined,
        thumbnailUrl: video.item.poster
          ? String(getFileUrl(video.item.poster))
          : undefined,
        uploadDate: data.value?.date_published,
        isFamilyFriendly: true,
        url: getImageUrl(video.item),
        contentUrl: getImageUrl(video.item),
        caption: video.item.caption,
        height: video.item.height,
        width: video.item.width,
        inLanguage: "pl",
        duration: video.item.duration,
        embedUrl: getImageUrl(video.item),

        license: "https://creativecommons.org/licenses/by-nc/4.0/",
        creditText: video.item.caption,
        acquireLicensePage:
          config.public.baseUrl +
          router.resolve(useLocalePath()({ name: "contact" })).href,
      })
    ),
]);

const openShare = (image?: GalleryItem) => {
  if (data.value && image?.collection === "images") {
    return sharePost(data.value, image.item);
  }
};

const { openAuthDialog, isLoggedIn } = useAuth();

const openSave = (image?: GalleryItem) => {
  if (!isLoggedIn.value) {
    savePostAfterLogin.value = data.value
      ? {
          post: data.value,
          image: image?.item,
        }
      : null;
    return openAuthDialog();
  }

  if (data.value && image?.collection === "images") {
    return savePost(data.value, image.item);
  }
};
</script>

<template>
  <PageBreadcrumbs v-bind="{ breadcrumbs }" />

  <div :class="['page', `page--${postType}-item`]">
    <div class="page__wrapper grow">
      <PostHeader :post="data" />
      <PostContent
        v-if="data"
        :post="data"
        @share-image="openShare"
        @like-image="openSave"
        @zoom-image="openLightbox"
        class="mt-[0.625rem] lg:mt-10 mb-6 lg:mb-[6.25rem]"
      />
      <PostCta :post="data" class="mb-10 lg:mb-[6.25rem]" />
      <PostGallery
        v-if="allMedia && allMedia.length > 0"
        :images="allMedia"
        @share-image="openShare"
        @like-image="openSave"
        @zoom-image="openLightbox"
        class="mb-8 lg:mb-[3.75rem]"
      />
      <PostLocation :post="data" class="mb-8 lg:mb-10" />
      <PostTags :post="data" class="mb-10 lg:mb-[6.25rem]" />
      <PostAuthor :post="data" class="mb-10 lg:mb-[6.25rem]" />
      <PostRelated :post="data" class="mb-[6.875rem] lg:mb-[6.25rem]" />
    </div>

    <PostNavigation v-bind="{ prev, next }" />
  </div>

  <ClientOnly>
    <ImageLightbox v-model:is-open="lightboxState.isOpen" :items="allMedia" />
  </ClientOnly>
</template>
